import React from 'react';
import { Box, Typography, Chip, Alert } from '@mui/material';
import {
  VideoLibrary,
  Description,
  Phone,
  Link as LinkIcon,
  Reply,
} from '@mui/icons-material';
import { colors } from '../theme/modernTheme';
import { getWhatsAppTemplateTypeIcon } from './WhatsAppTemplateTypeIcon';
import { formatDateForDisplay } from '../utils/dateUtils';


// Helper function to replace variables with their example values
const replaceVariablesWithExamples = (
  text,
  exampleParams,
  showExampleValues
) => {
  if (
    !text ||
    !showExampleValues ||
    !exampleParams ||
    exampleParams.length === 0
  ) {
    return text;
  }

  let result = text;
  exampleParams.forEach((param) => {
    const variablePattern = new RegExp(
      `\\{\\{${param.param_name}\\}\\}`,
      "g"
    );
    result = result.replace(
      variablePattern,
      param.example || `{{${param.param_name}}}`
    );
  });
  return result;
};

// Helper function to parse and render rich text formatting
const parseRichText = (text) => {
  if (!text) return text;

  const elements = [];
  let remainingText = text;
  let key = 0;

  // Regex patterns for formatting (in order of priority to avoid conflicts)
  const patterns = [
    { regex: /```([^`]+)```/g, Component: 'code', style: { fontFamily: 'monospace', backgroundColor: '#f3f4f6', padding: '2px 4px', borderRadius: '3px' } },
    { regex: /\*([^*]+)\*/g, Component: 'strong', style: { fontWeight: 'bold' } },
    { regex: /_([^_]+)_/g, Component: 'em', style: { fontStyle: 'italic' } },
    { regex: /~([^~]+)~/g, Component: 'span', style: { textDecoration: 'line-through' } },
  ];

  // Process text sequentially to handle all formatting
  while (remainingText.length > 0) {
    let earliestMatch = null;
    let earliestIndex = Infinity;
    let matchedPattern = null;

    // Find the earliest match among all patterns
    for (const pattern of patterns) {
      const regex = new RegExp(pattern.regex.source);
      const match = regex.exec(remainingText);
      if (match && match.index < earliestIndex) {
        earliestMatch = match;
        earliestIndex = match.index;
        matchedPattern = pattern;
      }
    }

    if (!earliestMatch) {
      // No more matches, add remaining text
      if (remainingText) {
        elements.push(<span key={key++}>{remainingText}</span>);
      }
      break;
    }

    // Add text before the match
    if (earliestIndex > 0) {
      elements.push(<span key={key++}>{remainingText.substring(0, earliestIndex)}</span>);
    }

    // Add the formatted text
    const Component = matchedPattern.Component;
    elements.push(
      <Component key={key++} style={matchedPattern.style}>
        {earliestMatch[1]}
      </Component>
    );

    // Update remaining text
    remainingText = remainingText.substring(earliestIndex + earliestMatch[0].length);
  }

  return <>{elements}</>;
};

/**
 * WhatsApp Template Preview Component
 * Displays WhatsApp template in a format similar to how it appears in WhatsApp
 * Supports: TEXT, IMAGE, VIDEO, DOCUMENT template types
 * Supports: URL, PHONE_NUMBER, QUICK_REPLY button types
 * 
 * @param {Object} template - The template object to preview
 * @param {boolean} showExampleValues - If true, replaces variables with their example values (for create/edit mode)
 */
export default function WhatsAppTemplatePreview({ template, showExampleValues = false }) {
  if (!template) {
    return (
      <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
        No template data available
      </Typography>
    );
  }

  // Extract components from template
  const components = template.components || [];
  const headerComponent = components.find(c => c.type === 'HEADER');
  const bodyComponent = components.find(c => c.type === 'BODY');
  const footerComponent = components.find(c => c.type === 'FOOTER');
  const buttonsComponent = components.find(c => c.type === 'BUTTONS');

  // Get header format
  const headerFormat = headerComponent?.format || 'TEXT';

  // Render header based on format
  const renderHeader = () => {
    if (!headerComponent) return null;

    switch (headerFormat) {
      case 'TEXT':
        const headerText = replaceVariablesWithExamples(
          headerComponent.text,
          headerComponent.example?.header_text_named_params,
          showExampleValues
        );
        return (
          <Box sx={{ px: 2, pt: 1.5, pb: 0.5 }}>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 700,
                fontSize: '1rem',
                color: '#000000',
                lineHeight: 1.4,
              }}>
              {headerText}
            </Typography>
          </Box>
        );
      
      case 'IMAGE':
        const imageUrl = headerComponent.example?.header_handle?.[0] || 'https://via.placeholder.com/300x150?text=Image';
        return (
          <Box sx={{ mb: 1, borderRadius: '8px 8px 0 0', overflow: 'hidden' }}>
            <Box
              component="img"
              src={imageUrl}
              alt="Template header"
              sx={{
                width: '100%',
                maxHeight: '200px',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          </Box>
        );
      
      case 'VIDEO':
        return (
          <Box sx={{ mb: 1, borderRadius: '8px 8px 0 0', overflow: 'hidden', backgroundColor: '#000' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '150px',
                color: '#fff',
              }}>
              <VideoLibrary sx={{ fontSize: 48, mr: 1 }} />
              <Typography variant="body2">Video</Typography>
            </Box>
          </Box>
        );
      
      case 'DOCUMENT':
        const docName = headerComponent.example?.header_text?.[0] || 'Document';
        return (
          <Box
            sx={{
              mb: 1,
              p: 1.5,
              backgroundColor: '#F3F4F6',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}>
            <Description sx={{ fontSize: 32, color: '#6B7280' }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#1F2937' }}>
                {docName}
              </Typography>
              <Typography variant="caption" sx={{ color: '#6B7280' }}>
                Document
              </Typography>
            </Box>
          </Box>
        );
      
      default:
        return null;
    }
  };

  const renderBodyText = () => {
    if (!bodyComponent) return '';

    const replacedVariablesText = replaceVariablesWithExamples(
      bodyComponent?.text,
      bodyComponent?.example?.body_text_named_params,
      showExampleValues
    );

    const parsedBodyElement = parseRichText(replacedVariablesText);

    return parsedBodyElement;
  }

  // Render buttons
  const renderButtons = () => {
    if (!buttonsComponent || !buttonsComponent.buttons) return null;

    return (
      <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {buttonsComponent.buttons.map((button, index) => {
          if (button.type === 'URL') {
            return (
              <Box
                key={index}
                sx={{
                  p: 1,
                  textAlign: 'center',
                  borderTop: '1px solid #E5E7EB',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#F9FAFB',
                  },
                }}>
                <LinkIcon sx={{ fontSize: 18, color: '#0EA5E9' }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: '#0EA5E9',
                    fontWeight: 500,
                    fontSize: '0.9375rem',
                  }}>
                  {button.text}
                </Typography>
              </Box>
            );
          }

          if (button.type === 'PHONE_NUMBER') {
            return (
              <Box
                key={index}
                sx={{
                  p: 1,
                  textAlign: 'center',
                  borderTop: '1px solid #E5E7EB',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#F9FAFB',
                  },
                }}>
                <Phone sx={{ fontSize: 18, color: '#0EA5E9' }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: '#0EA5E9',
                    fontWeight: 500,
                    fontSize: '0.9375rem',
                  }}>
                  {button.text}
                </Typography>
              </Box>
            );
          }

          if (button.type === 'QUICK_REPLY') {
            return (
              <Box
                key={index}
                sx={{
                  p: 1,
                  textAlign: 'center',
                  borderTop: '1px solid #E5E7EB',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 0.5,
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#F9FAFB',
                  },
                }}>
                <Reply sx={{ fontSize: 18, color: '#0EA5E9' }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: '#0EA5E9',
                    fontWeight: 500,
                    fontSize: '0.9375rem',
                  }}>
                  {button.text}
                </Typography>
              </Box>
            );
          }

          return null;
        })}
      </Box>
    );
  };

  return (
    <Box>
      {/* Template Info - Grid Layout */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 3,
          mb: 3,
        }}>
        <Box>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{
              mb: 0.5,
              fontWeight: 500,
              fontSize: '14px !important',
            }}>
            Template Name
          </Typography>
          <Typography
            variant="body2"
            color={colors.text.primary}
            sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
            {template.name}
          </Typography>
        </Box>

        <Box>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{
              mb: 0.5,
              fontWeight: 500,
              fontSize: '14px !important',
            }}>
            Language
          </Typography>
          <Typography
            variant="body2"
            color={colors.text.primary}
            sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
            {template.language}
          </Typography>
        </Box>

        <Box>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{
              mb: 0.5,
              fontWeight: 500,
              fontSize: '14px !important',
            }}>
            Category
          </Typography>
          <Typography
            variant="body2"
            color={colors.text.primary}
            sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
            {template.category || 'MARKETING'}
          </Typography>
        </Box>

        <Box>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{
              mb: 0.5,
              fontWeight: 500,
              fontSize: '14px !important',
            }}>
            Status
          </Typography>
          <Chip
            label={template.status}
            size="small"
            sx={{
              fontSize: '0.85rem',
              backgroundColor:
                template.status === 'APPROVED'
                  ? `${colors.success.main}15`
                  : template.status === 'PENDING'
                  ? `${colors.warning.main}15`
                  : `${colors.error.main}15`,
              color:
                template.status === 'APPROVED'
                  ? colors.success.main
                  : template.status === 'PENDING'
                  ? colors.warning.main
                  : colors.error.main,
              fontWeight: 600,
              borderRadius: '4px',
            }}
          />
        </Box>

        <Box>
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{
              mb: 0.5,
              fontWeight: 500,
              fontSize: '14px !important',
            }}>
            Type
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {getWhatsAppTemplateTypeIcon(template.type, { 
              fontSize: "small", 
              sx: { color: colors.text.secondary } 
            })}
            <Typography
              variant="body2"
              color={colors.text.primary}
              sx={{ fontWeight: 500, fontSize: '0.95rem', textTransform: 'capitalize' }}>
              {template.type?.toLowerCase() || 'Text'}
            </Typography>
          </Box>
        </Box>
          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{
                mb: 0.5,
                fontWeight: 500,
                fontSize: '14px !important',
              }}>
              Last Updated
            </Typography>
            <Typography
              variant="body2"
              color={colors.text.primary}
              sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
              { template?.last_updated_time ? formatDateForDisplay(template?.last_updated_time) : '-' }
            </Typography>
          </Box>
      </Box>

      {/* Rejection Reason Alert */}
      {template?.status === 'REJECTED' && template?.rejected_reason && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3,
            borderRadius: '4px',
          }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Rejection Reason
          </Typography>
          <Typography variant="body2">
            {template?.rejected_reason || 'No reason provided.'}
          </Typography>
        </Alert>
      )}

      {/* WhatsApp Preview */}
      <Box>
        <Typography
          variant="subtitle2"
          color="text.secondary"
          sx={{
            mb: 1,
            fontWeight: 500,
            fontSize: '14px !important',
          }}>
          WhatsApp Preview
        </Typography>
        
        {/* WhatsApp Message Bubble Container */}
        <Box
          sx={{
            p: 2,
            backgroundColor: '#F0E9E3',
            border: '1px solid #E2E8F0',
            borderRadius: '4px',
            backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(0,0,0,0.02) 10px, rgba(0,0,0,0.02) 20px)',
          }}>
          <Box
            sx={{
              maxWidth: '450px',
              backgroundColor: '#FFFFFF',
              borderRadius: '8px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
              overflow: 'hidden',
              position: 'relative',
            }}>
            {/* Header */}
            {renderHeader()}

            {/* Body */}
            {bodyComponent && (
              <Box sx={{ px: 2, py: headerComponent && headerFormat === 'TEXT' ? 0.5 : 1.5 }}>
                <Typography
                  component="div"
                  variant="body2"
                  sx={{
                    color: '#000000',
                    fontSize: '0.9375rem',
                    lineHeight: 1.5,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}>
                  {renderBodyText()}
                </Typography>
              </Box>
            )}

            {/* Footer */}
            {footerComponent && (
              <Box sx={{ px: 2, pb: 1 }}>
                <Typography
                  variant="caption"
                  sx={{
                    color: '#6B7280',
                    fontSize: '0.8125rem',
                  }}>
                  {footerComponent.text}
                </Typography>
              </Box>
            )}

            {/* Timestamp */}
            <Box sx={{ px: 2, pb: 1.5, display: 'flex', justifyContent: 'flex-end' }}>
              <Typography
                variant="caption"
                sx={{
                  color: '#9CA3AF',
                  fontSize: '0.7rem',
                }}>
                {new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false })}
              </Typography>
            </Box>

            {/* Buttons */}
            {renderButtons()}
          </Box>

          {/* Variables Info */}
          {bodyComponent?.text && bodyComponent.text.includes('{{') && (
            <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#FEF3C7', borderRadius: '6px', border: '1px solid #FCD34D' }}>
              <Typography variant="caption" sx={{ color: '#92400E', fontSize: '0.75rem' }}>
                <strong>Note:</strong> Variables like {'{{'} {'}'} will be replaced with actual contact data when sending.
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
